import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';

// A base de tudo o que abre POR CIMA do player (lista de exercícios, substituição, demonstração, confirmações).
//
// Não desmonta nada por baixo: o player continua montado (e com o estado da série que se estava a editar) enquanto isto está aberto, e fechar
// devolve o foco ao botão que o abriu. Em telemóvel sobe do fundo (bottom sheet); em ecrãs largos é uma janela ao centro.

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export default function Sheet({
  open, onClose, title, subtitle, children, footer, variant = 'sheet', dismissible = true, labelledBy, className = '', testId,
}) {
  const ref = useRef(null);
  const returnFocus = useRef(null);
  const autoId = useId();
  const titleId = labelledBy || `wp-sheet-title-${autoId}`;
  // Quem abre muda de função a cada render: o efeito não pode depender dela (senão perdia o botão a que devolve o foco).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    returnFocus.current = document.activeElement;
    document.body.classList.add('wp-lock');
    const node = ref.current;
    // O foco vai para o botão seguro (data-autofocus), quando o painel indica um; senão para o próprio painel.
    if (node) (node.querySelector('[data-autofocus]') || node).focus({ preventScroll: true });

    const onKey = (e) => {
      // Com dois painéis empilhados, só o de cima responde (Esc e Tab).
      const layers = document.querySelectorAll('.wp-sheet-layer');
      if (node && layers.length > 1 && layers[layers.length - 1] !== node.closest('.wp-sheet-layer')) return;
      if (e.key === 'Escape' && dismissible) { e.stopPropagation(); onCloseRef.current?.(); return; }
      if (e.key !== 'Tab' || !node) return;
      const items = [...node.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (items.length === 0) { e.preventDefault(); return; }
      const first = items[0]; const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === node)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      // Só levanta o bloqueio de rolagem se não houver outro painel aberto por cima/por baixo.
      if (document.querySelectorAll('.wp-sheet-layer').length <= 1) document.body.classList.remove('wp-lock');
      const back = returnFocus.current;
      if (back && typeof back.focus === 'function' && document.contains(back)) back.focus({ preventScroll: true });
    };
  }, [open, dismissible]);

  if (!open) return null;

  return (
    <div className={`wp-sheet-layer wp-sheet-${variant}`} data-testid={testId}>
      <div className="wp-sheet-backdrop" onClick={dismissible ? onClose : undefined} aria-hidden="true" />
      <div ref={ref} className={`wp-sheet ${className}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className="wp-sheet-grip" aria-hidden="true" />
        <header className="wp-sheet-head">
          <div className="wp-sheet-titles">
            <h2 id={titleId} className="wp-sheet-title">{title}</h2>
            {subtitle ? <p className="wp-sheet-subtitle">{subtitle}</p> : null}
          </div>
          {dismissible ? (
            <button type="button" className="wp-icon-btn" onClick={onClose} aria-label="Fechar"><X size={22} aria-hidden="true" /></button>
          ) : null}
        </header>
        <div className="wp-sheet-body">{children}</div>
        {footer ? <div className="wp-sheet-foot">{footer}</div> : null}
      </div>
    </div>
  );
}
